"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Clock, ShieldCheck, FileCheck2, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, Instagram, Facebook, Linkedin, Video } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function Advantages() {
  const shouldReduceMotion = useReducedMotion();
  const advantages = [{
    icon: Users,
    title: "Dedicated contracts managers",
    description: "Every project gets a single point of operational contact from initial site survey through to final handover and dismantling.",
    meta: "Direct site accountability"
  }, {
    icon: Clock,
    title: "Rapid response teams",
    description: "Responsive support for urgent site adaptations and safety re-inspections across the South East.",
    meta: "Prompt South East coverage"
  }, {
    icon: ShieldCheck,
    title: "Fully insured operations",
    description: "Insured operations covering commercial sites, public highways, and private properties.",
    meta: "Full contractor liability"
  }, {
    icon: FileCheck2,
    title: "Handover certificates & TG20 compliance",
    description: "Complete compliance documentation, scaffold handover certificates, and mandatory 7-day statutory safety inspection records.",
    meta: "NASC & TG20 standards"
  }];
  const highlights = ["NASC & CHAS accredited", "20+ years hands-on director leadership", "CISRS qualified scaffolding operatives"];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-bmf" data-nav="light" className="relative bg-background py-20 md:py-28 border-b border-border">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="h-1 w-8 bg-primary rounded-full" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Contractor Advantages
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default leading-tight">
            Why contractors and developers choose BMF
          </h2>

          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Practical scaffolding management built on reliable scheduling, verified safety accreditations, and direct family-run accountability.
          </p>
        </div>

        {/* Advantages 4-Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {advantages.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card rounded-lg p-6 flex flex-col justify-between shadow-xs hover:border-primary/60 transition-[border-color,box-shadow] duration-200">
                  <div>
                    <div className="size-12 rounded-md bg-accent flex items-center justify-center text-primary mb-5">
                      <IconComponent className="size-6" />
                    </div>

                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-lg md:text-xl font-bold text-card-foreground font-default">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </div>

                  <div className="pt-5 mt-6 border-t border-border/80 flex items-center justify-between text-xs font-semibold text-foreground/80">
                    <span>{item.meta}</span>
                    <span className="size-1.5 rounded-full bg-primary" />
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Banner / Action Block */}
        <div className="mt-12 md:mt-16 rounded-lg bg-secondary text-secondary-foreground p-8 md:p-10 border border-secondary flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h3 className="text-xl md:text-2xl font-bold font-default tracking-tight text-secondary-foreground mb-3">
              Ready to schedule scaffolding for your upcoming project?
            </h3>
            <p className="text-sm md:text-base text-secondary-foreground/80 mb-5 leading-relaxed">
              Get in touch for a site visit, technical advice, or a transparent, itemised quotation.
            </p>

            <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-xs md:text-sm text-secondary-foreground/90 font-medium">
              {highlights.map((point, index) => <div key={point} data-index={index} className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{point}</span>
                </div>)}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 h-12 shadow-sm transition-[background-color,transform] active:scale-[0.99]">
              <Link to="/ContactRequestQuotePage">
                <span>Request a Quote</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-semibold px-6 h-12">
              <Link to="/ContactRequestQuotePage">
                <PhoneCall className="size-4 mr-2 text-primary" />
                <span>Contact Team</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Social Proof Block */}
        <div className="mt-8 md:mt-10 rounded-lg bg-card border border-border p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="max-w-xl">
            <h4 className="text-lg md:text-xl font-bold font-default text-card-foreground">
              See our latest work
            </h4>
            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
              Follow our active site installations, weekly project documentation, and scaffolding updates across the South East.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Instagram */}
            <Button asChild variant="outline" size="icon" className="size-11 rounded-md border-border hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors">
              <Link to="https://www.instagram.com/bmfscaffolding/" newTab aria-label="BMF Scaffolding on Instagram">
                <Instagram className="size-5" />
              </Link>
            </Button>

            {/* Facebook */}
            <Button asChild variant="outline" size="icon" className="size-11 rounded-md border-border hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors">
              <Link to="https://www.facebook.com/profile.php?id=61564739075843" newTab aria-label="BMF Scaffolding on Facebook">
                <Facebook className="size-5" />
              </Link>
            </Button>

            {/* TikTok */}
            <Button asChild variant="outline" size="icon" className="size-11 rounded-md border-border hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors">
              <Link to="https://www.tiktok.com/@bmfscaffoldingltd" newTab aria-label="BMF Scaffolding on TikTok">
                <Video className="size-5" />
              </Link>
            </Button>

            {/* LinkedIn */}
            <Button asChild variant="outline" size="icon" className="size-11 rounded-md border-border hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors">
              <Link to="https://www.linkedin.com/in/peter-wood-61a9503aa/" newTab aria-label="Pete Wood / BMF Scaffolding on LinkedIn">
                <Linkedin className="size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}